import { useState, useCallback, useMemo } from 'react';
import { useCRM } from './useCRM';
import type { ContactStage, ContactSource } from '@/types/contacts';

// Types for analytics
export type DateRange = '7d' | '30d' | '90d' | 'custom';

export interface KPIData {
  totalRevenue: number;
  totalRevenueChange: number;
  newLeads: number;
  newLeadsChange: number;
  conversionRate: number;
  conversionRateChange: number;
  avgDealSize: number;
  avgDealSizeChange: number;
}

export interface PipelineData {
  stage: ContactStage;
  value: number;
  count: number;
  percentage: number;
}

export interface LeadSourceData {
  source: ContactSource | string;
  count: number;
  percentage: number;
  color: string;
}

export interface FunnelStage {
  name: string;
  value: number;
  count: number;
  dropOffRate: number;
  color: string;
}

export interface TeamMember {
  id: string;
  name: string;
  avatar?: string;
  dealsClosed: number;
  revenue: number;
  conversionRate: number;
  target: number;
}

export interface ActivityItem {
  id: string;
  type: 'email' | 'call' | 'meeting' | 'note' | 'deal' | 'status_change';
  title: string;
  description: string;
  contactName?: string;
  performedBy: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface RevenueTrend {
  date: string;
  revenue: number;
  deals: number;
}

export interface AnalyticsData {
  kpi: KPIData;
  pipeline: PipelineData[];
  leadSources: LeadSourceData[];
  funnel: FunnelStage[];
  teamPerformance: TeamMember[];
  activities: ActivityItem[];
  revenueTrend: RevenueTrend[];
}

// Stage colors
const STAGE_COLORS: Record<string, string> = {
  new: '#6B7280',
  qualified: '#3B82F6',
  proposal: '#F59E0B',
  negotiation: '#8B5CF6',
  closed_won: '#10B981',
  closed_lost: '#EF4444',
};

// Source colors
const SOURCE_COLORS: Record<string, string> = {
  'Website': '#2B7FFF',
  'Referral': '#10B981',
  'LinkedIn': '#8B5CF6',
  'Trade Show': '#F59E0B',
  'Cold Call': '#EF4444',
  'Email Campaign': '#EC4899',
  'Social Media': '#06B6D4',
  'RaiseBeacon Search': '#2B7FFF',
  'Other': '#6B7280',
};

// Export to CSV helper
export function exportToCSV(data: unknown[], filename: string) {
  if (data.length === 0) return;
  
  const headers = Object.keys(data[0] as object);
  const csvContent = [
    headers.join(','),
    ...data.map((row) =>
      headers
        .map((header) => {
          const value = (row as Record<string, unknown>)[header];
          if (value === null || value === undefined) return '';
          if (typeof value === 'string' && value.includes(',')) return `"${value}"`;
          return String(value);
        })
        .join(',')
    ),
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export function useAnalytics() {
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [customDateRange, setCustomDateRange] = useState<{ from?: Date; to?: Date }>({});
  const { contacts, isLoading: contactsLoading } = useCRM();

  // Safe contacts array
  const safeContacts = useMemo(() => contacts || [], [contacts]);

  // Calculate real KPI data from contacts
  const kpi = useMemo((): KPIData => {
    const totalRevenue = safeContacts
      .filter(c => c?.stage === 'closed_won')
      .reduce((sum, c) => sum + (c?.value || 0), 0);
    
    const newLeads = safeContacts.filter(c => {
      if (!c?.createdAt) return false;
      const created = new Date(c.createdAt);
      const daysAgo = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - daysAgo);
      return created >= cutoff;
    }).length;

    const wonDeals = safeContacts.filter(c => c?.stage === 'closed_won').length;
    const totalDeals = safeContacts.length;
    const conversionRate = totalDeals > 0 ? Math.round((wonDeals / totalDeals) * 100) : 0;

    const avgDealSize = wonDeals > 0 ? Math.round(totalRevenue / wonDeals) : 0;

    return {
      totalRevenue,
      totalRevenueChange: 0,
      newLeads,
      newLeadsChange: 0,
      conversionRate,
      conversionRateChange: 0,
      avgDealSize,
      avgDealSizeChange: 0,
    };
  }, [safeContacts, dateRange]);

  // Calculate real pipeline data
  const pipeline = useMemo((): PipelineData[] => {
    const stages: ContactStage[] = ['new', 'qualified', 'proposal', 'negotiation', 'closed_won', 'closed_lost'];
    const data = stages.map(stage => {
      const stageContacts = safeContacts.filter(c => c?.stage === stage);
      const value = stageContacts.reduce((sum, c) => sum + (c?.value || 0), 0);
      return {
        stage,
        value,
        count: stageContacts.length,
        percentage: 0,
      };
    });

    const totalValue = data.reduce((sum, d) => sum + d.value, 0);
    return data.map(d => ({
      ...d,
      percentage: totalValue > 0 ? Math.round((d.value / totalValue) * 100) : 0,
    }));
  }, [safeContacts]);

  // Calculate real lead source data - hide internal sources
  const leadSources = useMemo((): LeadSourceData[] => {
    // Don't show lead sources - this is internal data
    return [];
  }, [safeContacts]);

  // Calculate real funnel data
  const funnel = useMemo((): FunnelStage[] => {
    const total = safeContacts.length;
    const qualified = safeContacts.filter(c => ['qualified', 'proposal', 'negotiation', 'closed_won'].includes(c?.stage)).length;
    const proposal = safeContacts.filter(c => ['proposal', 'negotiation', 'closed_won'].includes(c?.stage)).length;
    const negotiation = safeContacts.filter(c => ['negotiation', 'closed_won'].includes(c?.stage)).length;
    const won = safeContacts.filter(c => c?.stage === 'closed_won').length;

    const stages = [
      { name: 'Total Leads', value: total, count: total, color: '#2B7FFF' },
      { name: 'Qualified', value: qualified, count: qualified, color: '#3B82F6' },
      { name: 'Proposal Sent', value: proposal, count: proposal, color: '#60A5FA' },
      { name: 'Negotiation', value: negotiation, count: negotiation, color: '#93C5FD' },
      { name: 'Closed Won', value: won, count: won, color: '#10B981' },
    ];

    return stages.map((stage, index) => {
      const prevValue = index === 0 ? stage.value : stages[index - 1].value;
      const dropOffRate = prevValue > 0 ? Math.round(((prevValue - stage.value) / prevValue) * 100) : 0;
      return { ...stage, dropOffRate };
    });
  }, [safeContacts]);

  // Real activities (from contact creation dates)
  const activities = useMemo((): ActivityItem[] => {
    return safeContacts
      .filter(c => c?.createdAt)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 10)
      .map((c) => ({
        id: `act_${c.id}`,
        type: 'deal' as const,
        title: 'Contact added',
        description: `${c.name || 'Unknown'} added to network`,
        contactName: c.name,
        performedBy: 'You',
        timestamp: c.createdAt,
      }));
  }, [safeContacts]);

  // Real revenue trend (group by creation date)
  const revenueTrend = useMemo((): RevenueTrend[] => {
    const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
    const data: RevenueTrend[] = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      const dayContacts = safeContacts.filter(c => {
        if (!c?.createdAt) return false;
        const created = new Date(c.createdAt).toISOString().split('T')[0];
        return created === dateStr;
      });

      data.push({
        date: dateStr,
        revenue: dayContacts.reduce((sum, c) => sum + (c?.value || 0), 0),
        deals: dayContacts.length,
      });
    }

    return data;
  }, [safeContacts, dateRange]);

  // Computed values
  const totalPipelineValue = useMemo(() => {
    return pipeline.reduce((sum, stage) => sum + stage.value, 0);
  }, [pipeline]);

  const weightedPipelineValue = useMemo(() => {
    return pipeline.reduce((sum, stage) => {
      const probability = stage.stage === 'closed_won' ? 1 : 
                         stage.stage === 'closed_lost' ? 0 :
                         stage.stage === 'negotiation' ? 0.7 :
                         stage.stage === 'proposal' ? 0.5 :
                         stage.stage === 'qualified' ? 0.3 : 0.1;
      return sum + stage.value * probability;
    }, 0);
  }, [pipeline]);

  // Generate team performance data from contacts
  const teamPerformance = useMemo((): TeamMember[] => {
    // Create a mock team member based on actual contact data
    const wonContacts = safeContacts.filter(c => c?.stage === 'closed_won');
    const totalRevenue = wonContacts.reduce((sum, c) => sum + (c?.value || 0), 0);
    const wonDeals = wonContacts.length;
    const totalContacts = safeContacts.length;
    
    if (totalContacts === 0) {
      return [];
    }
    
    return [{
      id: 'user_1',
      name: 'You',
      dealsClosed: wonDeals,
      revenue: totalRevenue,
      conversionRate: totalContacts > 0 ? Math.round((wonDeals / totalContacts) * 100) : 0,
      target: 100000,
    }];
  }, [safeContacts]);

  // Build data object
  const data: AnalyticsData | null = useMemo(() => {
    return {
      kpi,
      pipeline,
      leadSources,
      funnel,
      teamPerformance,
      activities,
      revenueTrend,
    };
  }, [kpi, pipeline, leadSources, funnel, teamPerformance, activities, revenueTrend]);

  // Refresh function
  const refresh = useCallback(() => {
    // Data updates automatically via useCRM
  }, []);

  // Export functions
  const exportPipeline = useCallback(() => {
    const exportData = pipeline.map((p) => ({
      Stage: p.stage,
      'Deal Count': p.count,
      'Total Value': p.value,
      Percentage: `${p.percentage}%`,
    }));
    exportToCSV(exportData, `pipeline-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`);
  }, [pipeline, dateRange]);

  const exportLeadSources = useCallback(() => {
    const exportData = leadSources.map((s) => ({
      Source: s.source,
      Count: s.count,
      Percentage: `${s.percentage}%`,
    }));
    exportToCSV(exportData, `lead-sources-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`);
  }, [leadSources, dateRange]);

  const exportTeamPerformance = useCallback(() => {
    const exportData = data?.teamPerformance.map((m) => ({
      Name: m.name,
      'Deals Closed': m.dealsClosed,
      Revenue: m.revenue,
      'Conversion Rate': `${m.conversionRate}%`,
      Target: m.target,
    })) || [];
    exportToCSV(exportData, `team-performance-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`);
  }, [data?.teamPerformance, dateRange]);

  const exportRevenueTrend = useCallback(() => {
    const exportData = revenueTrend.map((r) => ({
      Date: r.date,
      Revenue: r.revenue,
      Deals: r.deals,
    }));
    exportToCSV(exportData, `revenue-trend-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`);
  }, [revenueTrend, dateRange]);

  return {
    dateRange,
    setDateRange,
    customDateRange,
    setCustomDateRange,
    isLoading: contactsLoading,
    data,
    activities,
    totalPipelineValue,
    weightedPipelineValue,
    refresh,
    exportPipeline,
    exportLeadSources,
    exportTeamPerformance,
    exportRevenueTrend,
  };
}

export default useAnalytics;
